import { ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { memberCheckTypeToReadableMap } from '@domainConstants'
import { LinearProgress, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { DataGrid, gridClasses } from '@mui/x-data-grid'
import type { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { getElementUtilizationFromStructuralChecks } from '@editor/components/ElementCSTable/utils'
import { UtilizationC90TextIcon, UtilizationTextIcon } from '@ui/icons/misc'
import { useElementLabel } from '@editorHooks'
import { CheckSettingsSectionKey } from '../constants'
import useLiveCheckRecompute from '../hooks/useLiveCheckRecompute'
import { CheckSettingsFormValues, CheckSettingsRow, MultiEditCheckSettingsElement } from '../types'

export const formatUtilization = (value: number | undefined): string =>
  value !== undefined && Number.isFinite(value) ? value.toFixed(2) : ''

export const utilizationCellClassName = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) return ''
  if (value >= SLIGHTLY_OVERUTILIZED_THRESHOLD) return 'overutilized'
  if (value >= 1.0) return 'slightly-overutilized'
  return ''
}

export const utilizationGridSx = (theme: Theme) => ({
  [`& .${gridClasses.cell}.slightly-overutilized`]: {
    backgroundColor: theme.results.utilization.slightlyOverutilized.light,
  },
  [`& .${gridClasses.cell}.overutilized`]: {
    backgroundColor: theme.results.utilization.overutilized.light,
  },
})

export const renderUtilizationCell = (
  value: number | undefined,
  checkType: MemberCheckType | undefined,
  dataCy: string,
): ReactElement => {
  const available = value !== undefined && Number.isFinite(value)
  const display = available ? formatUtilization(value) : 'n/a'
  const title = checkType ? memberCheckTypeToReadableMap[checkType] ?? checkType : 'n/a'

  return (
    <Tooltip title={title}>
      <Typography
        sx={{ fontWeight: 'inherit', lineHeight: 1, display: 'flex', alignItems: 'center' }}
        data-cy={dataCy}
      >
        {display}
      </Typography>
    </Tooltip>
  )
}

const etaColumn: GridColDef<CheckSettingsRow> = {
  field: 'eta',
  headerName: 'η',
  width: 56,
  minWidth: 56,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => <UtilizationTextIcon />,
  renderCell: (params: GridRenderCellParams<CheckSettingsRow>) =>
    renderUtilizationCell(
      params.row.eta,
      params.row.etaCheckType,
      `check-settings-table-eta-${params.row.elementGuid}`,
    ),
  cellClassName: (params: GridCellParams<CheckSettingsRow>) =>
    utilizationCellClassName(params.row.eta),
  display: 'flex',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
}

const etaCColumn: GridColDef<CheckSettingsRow> = {
  field: 'etaC',
  headerName: 'ηc,90',
  width: 64,
  minWidth: 64,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => <UtilizationC90TextIcon />,
  renderCell: (params: GridRenderCellParams<CheckSettingsRow>) =>
    renderUtilizationCell(
      params.row.etaC,
      params.row.etaCCheckType,
      `check-settings-table-eta-c-${params.row.elementGuid}`,
    ),
  cellClassName: (params: GridCellParams<CheckSettingsRow>) =>
    utilizationCellClassName(params.row.etaC),
  display: 'flex',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
}

interface Props {
  /** Distinguishes this section's recompute mutation from the other tabs'. */
  sectionKey: CheckSettingsSectionKey
  /** Columns describing the section's own settings, rendered between label and η. */
  settingsColumns: GridColDef<CheckSettingsRow>[]
  /** Values for {@link settingsColumns}, previewing the staged settings when there are any. */
  buildSettingsRow: (
    element: MultiEditCheckSettingsElement,
    sharedSettings: SettingsOnMember | undefined,
  ) => Record<string, string>
  /** Applies the staged settings to a member — the same merge the save uses. */
  mergeSettings: (existing: SettingsOnMember, shared: SettingsOnMember) => SettingsOnMember
  /** Blocks the recompute while the staged settings are incomplete. */
  isFormValid?: (settings: SettingsOnMember | undefined) => boolean
}

/**
 * Shows each selected member's current settings alongside its utilization, and
 * recomputes the checks live as the shared settings change so an engineer sees
 * the effect of a change before saving it.
 */
const CheckSettingsElementTable = ({
  sectionKey,
  settingsColumns,
  buildSettingsRow,
  mergeSettings,
  isFormValid,
}: Props): ReactElement => {
  const theme = useTheme()
  const getLabel = useElementLabel()

  const elements = useWatch<CheckSettingsFormValues>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']
  const sharedSettings = useWatch<CheckSettingsFormValues>({ name: 'sharedSettings' }) as
    | SettingsOnMember
    | undefined

  const showEtaColumns = useMemo(
    () => elements.some(element => element.checks.length > 0),
    [elements],
  )

  const { isUpdatingChecks } = useLiveCheckRecompute({ sectionKey, mergeSettings, isFormValid })

  const columns = useMemo<GridColDef<CheckSettingsRow>[]>(() => {
    const base: GridColDef<CheckSettingsRow>[] = [
      { field: 'label', headerName: 'Element', flex: 0, minWidth: 80 },
      ...settingsColumns,
    ]

    if (showEtaColumns) base.push(etaColumn, etaCColumn)

    return base
  }, [settingsColumns, showEtaColumns])

  const rows = useMemo<CheckSettingsRow[]>(
    () =>
      elements.map(element => {
        const utilization = getElementUtilizationFromStructuralChecks(element.guid, element.checks)

        return {
          elementGuid: element.guid,
          label: getLabel(element.guid),
          ...buildSettingsRow(element, sharedSettings),
          eta: utilization.eta?.max_utilization,
          etaC: utilization.etaC?.max_utilization,
          etaCheckType: utilization.eta?.check_type,
          etaCCheckType: utilization.etaC?.check_type,
        }
      }),
    [elements, sharedSettings, getLabel, buildSettingsRow],
  )

  return (
    <Stack direction="column" spacing={0.5} pt={1}>
      {isUpdatingChecks && <LinearProgress />}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={row => row.elementGuid}
        hideFooterPagination
        hideFooterSelectedRowCount
        hideFooter
        autosizeOnMount
        autosizeOptions={{ includeHeaders: false, expand: true }}
        disableColumnSorting
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        density="compact"
        sx={utilizationGridSx(theme)}
      />
    </Stack>
  )
}

export default CheckSettingsElementTable
